import { formatNumber, parseNumber } from "./number_helper"
import { Chart } from "chart.js"
import pattern from "patternomaly"
import { buildTooltip } from "./dashboard_tooltip"

const colors = [
  "#00bae6",
  "#aee907",
  "#e95207",
  "#00647a",
  "#418c9c",
  "#fba87e",
  "#fdfdc4",
  "#d4fa6b",
  "#860452",
  "#fa57c1",
  "#002e38",
]

const getColor = (i) => {
  if (i < colors.length) {
    return colors[i]
  } else {
    return "#" + (Math.random().toString(16) + "0000000").slice(2, 8)
  }
}

const stackTranslations = {
  T: "Technologien",
  ZQ: "Zusatzqualitäten",
  H: "Herkunft",
}

const productGraphConfig = (report) => {
  const technologyData = Object.entries(report.by_technology).flatMap(
    ([technology, values], i) => {
      const color = getColor(i)

      const total = parseNumber(values.total)
      const connected = parseNumber(values.connected)
      const amountLeft = Math.max(total - connected, 0)

      return [
        {
          label: technology,
          data: [connected],
          backgroundColor: color,
          stack: "T",
        },
        {
          label: `${technology} (Nicht zugewiesen)`,
          data: [amountLeft],
          backgroundColor: pattern.draw(
            "diagonal-right-left",
            color,
            "#ffffff",
            15,
          ),
          stack: "T",
        },
      ]
    },
  )

  const qualityData = Object.entries(report.by_quality).flatMap(
    ([quality, values], i) => {
      const color = getColor(i)

      const total = parseNumber(values.total)
      const connected = parseNumber(values.connected)
      const amountLeft = Math.max(total - connected, 0)

      return [
        {
          label: quality,
          data: [connected],
          backgroundColor: color,
          stack: "ZQ",
        },
        {
          label: `${quality} (Nicht zugewiesen)`,
          data: [amountLeft],
          backgroundColor: pattern.draw(
            "diagonal-right-left",
            color,
            "#ffffff",
            15,
          ),
          stack: "ZQ",
        },
      ]
    },
  )

  const originData = Object.entries(report.by_origin).flatMap(
    ([origin, values], i) => {
      const color = getColor(i)

      const total = parseNumber(values.total)
      const connected = parseNumber(values.connected)
      const amountLeft = Math.max(total - connected, 0)

      return [
        {
          label: origin,
          data: [connected],
          backgroundColor: color,
          stack: "H",
        },
        {
          label: `${origin} (Nicht zugewiesen)`,
          data: [amountLeft],
          backgroundColor: pattern.draw(
            "diagonal-right-left",
            color,
            "#ffffff",
            15,
          ),
          stack: "H",
        },
      ]
    },
  )

  return {
    data: {
      labels: [""],
      datasets: [...technologyData, ...qualityData, ...originData],
    },
    options: {
      locale: "de-CH",
      animations: { y: { easing: "easeOutBounce" } },
      scales: {
        x: {
          grid: { display: false, color: Chart.defaults.color },
          border: { color: Chart.defaults.color },
          stacked: true,
        },
        y: {
          grid: { drawOnChartArea: false, color: Chart.defaults.color },
          border: { color: Chart.defaults.color },
          stacked: true,
          beginAtZero: true,
        },
      },
      plugins: {
        legend: { display: false },
        title: { display: true, text: report.title, position: "bottom" },
        tooltip: {
          enabled: false,
          external: (context) => {
            const { tooltip } = context
            const stack = tooltip.dataPoints[0].dataset.stack

            let relevantKey
            if (stack === "T") {
              relevantKey = "by_technology"
            } else if (stack === "ZQ") {
              relevantKey = "by_quality"
            } else if (stack === "H") {
              relevantKey = "by_origin"
            }

            const total = Object.values(report[relevantKey]).reduce(
              (sum, entry) => sum + parseNumber(entry.total),
              0,
            )

            // Head (title)
            const head = `${report.title} (${stackTranslations[stack]})`

            // Body
            const body = Object.entries(report[relevantKey]).flatMap(
              ([key, entry]) => {
                const amount = parseNumber(entry.total)
                const connected = parseNumber(entry.connected)
                const amountLeft = amount - connected

                const relativeAmount = total === 0 ? 1 : amount / total
                const relativeAmountLeft = total === 0 ? 1 : amountLeft / total

                const row = [
                  [
                    key,
                    `${formatNumber(amount, { maximumFractionDigits: 0 })} MWh`,
                    `${formatNumber(relativeAmount * 100, { maximumFractionDigits: 2 })}%`,
                  ],
                ]

                if (amountLeft > 0) {
                  row.push([
                    "\t ↳ davon ohne Zuweisung",
                    `${formatNumber(amountLeft, { maximumFractionDigits: 0 })} MWh`,
                    `${formatNumber(relativeAmountLeft * 100, { maximumFractionDigits: 2 })}%`,
                  ])
                }

                if (amountLeft < 0) {
                  row.push([
                    "\t ↳ davon überdeckt",
                    `${formatNumber(-amountLeft, { maximumFractionDigits: 0 })} MWh`,
                    `${formatNumber(-relativeAmountLeft * 100, { maximumFractionDigits: 2 })}%`,
                  ])
                }

                return row
              },
            )

            // Foot (sum row)
            const foot = [
              "Total:",
              `${formatNumber(total, { maximumFractionDigits: 0 })} MWh`,
              "100%",
            ]

            buildTooltip(
              context,
              {
                head,
                body,
                foot,
              },
              { showColor: true },
            )
          },
        },
        datalabels: {
          align: "start",
          anchor: "start",
          formatter: (_, context) => {
            const datasets = context.chart.data.datasets
            const dataset = datasets[context.datasetIndex]
            const previousDataset = datasets[context.datasetIndex - 1]

            // only show label for the first dataset of each stack
            if (!previousDataset || previousDataset.stack !== dataset.stack) {
              return dataset.stack
            } else {
              return ""
            }
          },
          offset: 4,
          listeners: {
            enter: (context) => {
              context.chart.canvas.title =
                stackTranslations[context.dataset.stack]
            },
            leave: (context) => {
              context.chart.canvas.title = ""
            },
          },
        },
      },
    },
  }
}

export { productGraphConfig }
